import type { Dayjs } from 'dayjs';

export type TimeRangeValue = [Dayjs | null, Dayjs | null] | null;

export type TimeRangeError = 'missing' | 'sameStartAndEnd';

const minutesOfDay = (time: Dayjs): number => time.hour() * 60 + time.minute();

export const resolveTimeRange = (
  value: TimeRangeValue | undefined,
  durationMinutes?: number
): [Dayjs, Dayjs | null] | undefined => {
  const [startTime, endTime] = value ?? [];

  if (!startTime) {
    return undefined;
  }

  return [
    startTime,
    durationMinutes
      ? startTime.add(durationMinutes, 'minute')
      : (endTime ?? null),
  ];
};

export const validateTimeRange = (
  timeRange: [Dayjs | null, Dayjs | null] | undefined,
  durationMinutes?: number
): TimeRangeError | null => {
  const [startTime, endTime] = timeRange ?? [];

  if (!startTime || !endTime) {
    return 'missing';
  }

  if (!durationMinutes && minutesOfDay(endTime) === minutesOfDay(startTime)) {
    return 'sameStartAndEnd';
  }

  return null;
};

export const endsNextDay = (
  timeRange: [Dayjs | null, Dayjs | null] | undefined,
  durationMinutes?: number
): boolean => {
  const [startTime, endTime] = timeRange ?? [];

  if (!startTime || !endTime) {
    return false;
  }

  return durationMinutes
    ? minutesOfDay(startTime) + durationMinutes >= 24 * 60
    : minutesOfDay(endTime) < minutesOfDay(startTime);
};

export const TIME_RANGE_ERROR_MESSAGES: Record<TimeRangeError, string> = {
  missing: 'Time window is required to save',
  sameStartAndEnd: 'End time must differ from start time',
};
